import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight, ChevronUp, ChevronDown, ArrowUpDown, MapPin } from 'lucide-react';
import { getStatusName } from '../../../services/apiService';
import { ESTATUS } from '../../../config/config';
import {
    formatDate,
    formatDateTime,
    getColorCode,
    getStatusBadgeColor,
    isEnSitio
} from '../../../utils/vehicleHelpers';
import CarTraceabilityPopUp from '../../popups/CarTraceabilityPopUp';

/**
 * Tabla de vehículos con paginación y ordenamiento
 * 
 * Features:
 * - Ordenamiento por cualquier columna (ASC/DESC)
 * - Paginación con 15 elementos por página
 * - Navegación inteligente de páginas (1, 2, 3, ..., última)
 * - Reset automático de página cuando cambia el filtro
 * - Indicadores visuales de estado (en sitio/fuera)
 * - Badges con colores según marca, estatus, etc.
 * - Columnas dinámicas
 * 
 * @component
 * @param {Object} props
 * @param {Array} props.vehiculos - Array de vehículos a mostrar
 * @param {Array} props.visibleColumns - Columnas visibles configuradas
 */
const VehiclesTable = ({ vehiculos, visibleColumns, disableRowClick = false, itemsPerPage = 15, isSalesDashboard = false }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [sortConfig, setSortConfig] = useState({ key: 'ultimaLectura', direction: 'desc' });
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const theadRef = useRef(null);
    const scrollWrapperRef = useRef(null);
    const [isHeaderFixed, setIsHeaderFixed] = useState(false);
    const [fixedMetrics, setFixedMetrics] = useState({ left: 0, width: 0, colWidths: [], scrollLeft: 0 });

    /**
     * Ordenamiento de vehículos según configuración actual
     * Soporta ordenamiento por strings, números y fechas
     */
    const sortedVehiculos = useMemo(() => {
        if (!sortConfig.key) return vehiculos;

        return [...vehiculos].sort((a, b) => {
            let aValue, bValue;

            // Mapeo de keys a propiedades del vehículo
            switch (sortConfig.key) {
                case 'vin':
                    aValue = a.VIN;
                    bValue = b.VIN;
                    break;
                case 'marca':
                    aValue = a.Marca;
                    bValue = b.Marca;
                    break;
                case 'familia':
                    aValue = a.Familia || a.Modelo;
                    bValue = b.Familia || b.Modelo;
                    break;
                case 'anoModelo':
                    aValue = a.Año || a.AnoModelo;
                    bValue = b.Año || b.AnoModelo;
                    break;
                case 'catalogo':
                    aValue = a.Catalogo || a.Version;
                    bValue = b.Catalogo || b.Version;
                    break;
                case 'colorExt':
                    aValue = a.ColorExterior || a.Color;
                    bValue = b.ColorExterior || b.Color;
                    break;
                case 'colorInt':
                    aValue = a.ColorInterior || a.ColorInt;
                    bValue = b.ColorInterior || b.ColorInt;
                    break;
                case 'patio':
                    aValue = a.BID;
                    bValue = b.BID;
                    break;
                case 'ubicacion':
                    aValue = a.Ubicacion || a.lectura_rfid?.subpatio_nombre;
                    bValue = b.Ubicacion || b.lectura_rfid?.subpatio_nombre;
                    break;
                case 'status':
                    aValue = getStatusName(a.estatus);
                    bValue = getStatusName(b.estatus);
                    break;
                case 'estado':
                    aValue = a.Estado || a.lectura_rfid?.patio_estado;
                    bValue = b.Estado || b.lectura_rfid?.patio_estado;
                    break;
                case 'rfid':
                    aValue = a.detectado_rfid ? 1 : 0;
                    bValue = b.detectado_rfid ? 1 : 0;
                    break;
                case 'ultimaLectura':
                    aValue = a.lectura_rfid?.last_read;
                    bValue = b.lectura_rfid?.last_read;
                    break;
                case 'enSitio':
                    aValue = isEnSitio(a) ? 1 : 0;
                    bValue = isEnSitio(b) ? 1 : 0;
                    break;
                case 'fecha':
                    aValue = a.FechaRecibo || a.fecharecibo;
                    bValue = b.FechaRecibo || b.fecharecibo;
                    break;
                case 'sitio':
                    aValue = a.sitio;
                    bValue = b.sitio;
                    break;
                case 'subSitio':
                    aValue = a.subSitio;
                    bValue = b.subSitio;
                    break;
                case 'observaciones':
                    aValue = a.Observaciones;
                    bValue = b.Observaciones;
                    break;
                case 'situacion':
                    aValue = a.Situacion;
                    bValue = b.Situacion;
                    break;
                case 'diasAntiguedad':
                    aValue = Number(a.diasAntiguedad) || 0;
                    bValue = Number(b.diasAntiguedad) || 0;
                    break;
                case 'descripcion':
                    aValue = a.Descripcion;
                    bValue = b.Descripcion;
                    break;
                case 'equipamiento':
                    aValue = a.Equipamiento;
                    bValue = b.Equipamiento;
                    break;
                case 'status':
                    aValue = a.status;
                    bValue = b.status;
                    break;
                default:
                    aValue = a[sortConfig.key];
                    bValue = b[sortConfig.key];
            }

            // Manejo de valores null/undefined
            if (aValue === null || aValue === undefined) return 1;
            if (bValue === null || bValue === undefined) return -1;

            let result = 0;

            // Ordenamiento especial para fechas — soporta DD/MM/YYYY e ISO
            if (sortConfig.key === 'ultimaLectura' || sortConfig.key === 'fecha') {
                const parseDate = (val) => {
                    if (!val) return -1;
                    const ddmm = typeof val === 'string' && /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(val);
                    if (ddmm) {
                        const d = new Date(parseInt(ddmm[3]), parseInt(ddmm[2]) - 1, parseInt(ddmm[1]));
                        return isNaN(d.getTime()) ? -1 : d.getTime();
                    }
                    const d = new Date(val);
                    return isNaN(d.getTime()) ? -1 : d.getTime();
                };
                result = parseDate(aValue) - parseDate(bValue);
            } else if (typeof aValue === 'string') {
                result = aValue.localeCompare(bValue);
            } else {
                result = aValue - bValue;
            }

            return sortConfig.direction === 'desc' ? -result : result;
        });
    }, [vehiculos, sortConfig]);

    // Cálculos de paginación
    const totalPages = Math.ceil(sortedVehiculos.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedVehiculos = sortedVehiculos.slice(startIndex, endIndex);

    /**
     * Maneja el click en un header de columna para ordenar
     */
    const handleSort = (key) => {
        setSortConfig(current => ({
            key,
            direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    /**
     * Obtiene el ícono de ordenamiento para una columna
     */
    const getSortIcon = (columnKey) => {
        if (sortConfig.key !== columnKey) {
            return <ArrowUpDown className="w-4 h-4 text-gray-400" />;
        }
        return sortConfig.direction === 'asc'
            ? <ChevronUp className="w-4 h-4 text-blue-600" />
            : <ChevronDown className="w-4 h-4 text-blue-600" />;
    };

    const captureMetrics = useCallback(() => {
        if (!theadRef.current || !scrollWrapperRef.current) return;
        const rect = scrollWrapperRef.current.getBoundingClientRect();
        const ths = Array.from(theadRef.current.querySelectorAll('th'));
        setFixedMetrics({
            left: rect.left,
            width: rect.width,
            colWidths: ths.map(th => th.getBoundingClientRect().width),
            scrollLeft: scrollWrapperRef.current.scrollLeft,
        });
    }, []);

    useEffect(() => {
        if (!isSalesDashboard || !theadRef.current) return;
        const observer = new IntersectionObserver(([entry]) => {
            const fixed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            setIsHeaderFixed(fixed);
            if (fixed) captureMetrics();
        }, { threshold: 0 });
        observer.observe(theadRef.current);
        return () => observer.disconnect();
    }, [isSalesDashboard, captureMetrics]);

    useEffect(() => {
        if (!isSalesDashboard || !isHeaderFixed || !scrollWrapperRef.current) return;
        const el = scrollWrapperRef.current;
        const onHScroll = () => setFixedMetrics(prev => ({ ...prev, scrollLeft: el.scrollLeft }));
        el.addEventListener('scroll', onHScroll, { passive: true });
        return () => el.removeEventListener('scroll', onHScroll);
    }, [isSalesDashboard, isHeaderFixed]);

    useEffect(() => {
        if (!isSalesDashboard) return;
        const onResize = () => { if (isHeaderFixed) captureMetrics(); };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [isSalesDashboard, isHeaderFixed, captureMetrics]);

    // Funciones de navegación de paginación
    const goToPage = (page) => { setCurrentPage(page); if (isSalesDashboard) window.scrollTo({ top: 0, behavior: 'smooth' }); };
    const goToPrevPage = () => { setCurrentPage(prev => Math.max(prev - 1, 1)); if (isSalesDashboard) window.scrollTo({ top: 0, behavior: 'smooth' }); };
    const goToNextPage = () => { setCurrentPage(prev => Math.min(prev + 1, totalPages)); if (isSalesDashboard) window.scrollTo({ top: 0, behavior: 'smooth' }); };

    /**
     * Genera array de números de página con ellipsis (...)
     * Ejemplo: [1, 2, 3, ..., 10] o [1, ..., 5, 6, 7, ..., 10]
     */
    const getPageNumbers = (isMobile = false) => {
        const pages = [];
        const totalNumbersToShow = isMobile ? 3 : 5;
        const delta = isMobile ? 1 : 2;

        if (totalPages <= totalNumbersToShow) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            const startPage = Math.max(currentPage - delta, 1);
            const endPage = Math.min(currentPage + delta, totalPages);

            if (startPage > 1) pages.push(1);
            if (startPage > 2) pages.push('...');

            for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
            }

            if (endPage < totalPages - 1) pages.push('...');
            if (endPage < totalPages) pages.push(totalPages);
        }

        return pages;
    };

    /**
     * Reset de página cuando cambia el filtro de vehículos
     */
    useEffect(() => {
        setCurrentPage(1);
    }, [vehiculos.length]);

    // Renderizador de celdas dinámico
    const renderCell = (vehicle, columnId) => {
        const enSitio = isEnSitio(vehicle);

        switch (columnId) {
            case 'vin':
                return <span className="font-medium text-gray-900">{vehicle.VIN}</span>;
            case 'marca':
                return <span>{vehicle.Marca}</span>;
            case 'familia':
                return <span>{vehicle.Familia || vehicle.Modelo}</span>;
            case 'anoModelo':
                return <span>{vehicle.Año || vehicle.AnoModelo}</span>;
            case 'catalogo':
                return <span>{vehicle.Catalogo || vehicle.Version}</span>;
            case 'colorExt':
                return (
                    <div className="flex items-center">
                        <div className="w-3 h-3 rounded-full mr-2"
                            style={{ backgroundColor: getColorCode(vehicle.ColorExterior || vehicle.Color) }}>
                        </div>
                        {vehicle.ColorExterior || vehicle.Color}
                    </div>
                );
            case 'colorInt':
                return (
                    <div className="flex items-center">
                        <div className="w-3 h-3 rounded-full mr-2"
                            style={{ backgroundColor: getColorCode(vehicle.ColorInterior || vehicle.ColorInt) }}>
                        </div>
                        {vehicle.ColorInterior || vehicle.ColorInt || '-'}
                    </div>
                );
            case 'patio':
                return (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {vehicle.BID}
                    </span>
                );
            case 'ubicacion':
                return (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        {vehicle.Ubicacion || vehicle.lectura_rfid?.subpatio_nombre || '-'}
                    </span>
                );
            case 'estatus':
                return (
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(vehicle.estatus)} whitespace-nowrap`}>
                        {getStatusName(vehicle.estatus)}
                    </span>
                );
            case 'VTE_DOCTO':
                const vteDocto = vehicle.VTE_DOCTO || vehicle.vte_docto;
                return <span className="text-sm text-gray-900 whitespace-nowrap">{vteDocto || '-'}</span>;
            case 'vendedor':
                const vendedorVal = vehicle.vendedor || vehicle.Vendedor;
                return vendedorVal ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 whitespace-nowrap">
                        {vendedorVal}
                    </span>
                ) : (
                    <span>-</span>
                );
            case 'estado':
                return <span>{vehicle.Estado || vehicle.lectura_rfid?.patio_estado || '-'}</span>;
            case 'ultimaLectura':
                return <span className="text-xs text-gray-500">{formatDateTime(vehicle.lectura_rfid?.last_read)}</span>;
            case 'enSitio':
                return (
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${enSitio
                        ? 'bg-green-100 text-green-700'
                        : 'bg-orange-100 text-orange-700'
                        }`}>
                        {enSitio ? (
                            <>
                                <MapPin className="w-3 h-3 mr-1" />
                                En sitio
                            </>
                        ) : (
                            <>
                                <MapPin className="w-3 h-3 mr-1" />
                                Fuera
                            </>
                        )}
                    </span>
                );
            case 'fecha':
                return <span className="text-gray-500">{formatDate(vehicle.FechaRecibo || vehicle.fecharecibo)}</span>;
            case 'sitio':
                return vehicle.sitio
                    ? <span className="text-gray-900 text-sm">{vehicle.sitio}</span>
                    : <span className="text-gray-400">-</span>;
            case 'subSitio':
                return vehicle.subSitio
                    ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">{vehicle.subSitio}</span>
                    : <span className="text-gray-400">-</span>;
            case 'status': {
                const statusNames = { 1: 'Inventario', 2: 'No Mayoreado', 3: 'Por llegar', 6: 'Fact. no entregado', 7: 'Entregado' };
                const statusColors = { 1: 'bg-blue-100 text-blue-800', 2: 'bg-orange-100 text-orange-800', 3: 'bg-purple-100 text-purple-800', 6: 'bg-pink-100 text-pink-800', 7: 'bg-red-100 text-red-800' };
                const name = statusNames[vehicle.status];
                const color = statusColors[vehicle.status] || 'bg-gray-100 text-gray-600';
                return name
                    ? <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color}`}>{name}</span>
                    : <span className="text-gray-400">{vehicle.status ?? '-'}</span>;
            }
            case 'observaciones':
                return vehicle.Observaciones
                    ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">{vehicle.Observaciones}</span>
                    : <span className="text-gray-400">-</span>;
            case 'situacion': {
                if (!vehicle.Situacion) return <span className="text-gray-400">-</span>;
                let badgeClass = "bg-green-100 text-green-800";
                if (isSalesDashboard) {
                    const sitLower = vehicle.Situacion.toLowerCase();
                    if (sitLower.includes('demo')) {
                        badgeClass = "bg-yellow-200 text-yellow-800";
                    }
                }
                return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badgeClass}`}>{vehicle.Situacion}</span>;
            }
            case 'diasAntiguedad': {
                const dias = Number(vehicle.diasAntiguedad);
                const color = dias > 180 ? 'bg-red-100 text-red-800'
                    : dias > 90 ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-green-100 text-green-800';
                return vehicle.diasAntiguedad != null
                    ? <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color}`}>{dias}d</span>
                    : <span className="text-gray-400">-</span>;
            }
            case 'descripcion':
                return <span className="text-gray-700 text-xs">{vehicle.Descripcion || '-'}</span>;
            case 'equipamiento':
                return <span className="text-gray-700 text-xs">{vehicle.Equipamiento || '-'}</span>;
            default:
                return <span>-</span>;
        }
    };

    const totalFixedWidth = fixedMetrics.colWidths.reduce((s, w) => s + w, 0);

    return (
        <>
            {isSalesDashboard && isHeaderFixed && fixedMetrics.colWidths.length > 0 && (
                <>
                    <div style={{ position: 'fixed', top: 0, left: fixedMetrics.left, width: fixedMetrics.width, height: '12px', zIndex: 50, backgroundColor: '#f9fafb' }} />
                    <div style={{ position: 'fixed', top: '12px', left: fixedMetrics.left, width: fixedMetrics.width, zIndex: 50, overflow: 'hidden', backgroundColor: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                        <table style={{ tableLayout: 'fixed', width: totalFixedWidth, transform: `translateX(${-fixedMetrics.scrollLeft}px)`, borderCollapse: 'collapse' }}>
                            <thead className="bg-white shadow-sm" style={{ borderBottom: '1px solid #e5e7eb' }}>
                                <tr>
                                    {visibleColumns.map((column, i) => (
                                        <th
                                            key={column.id}
                                            style={{ width: fixedMetrics.colWidths[i] }}
                                            className={`bg-white px-2 sm:px-4 pt-4 pb-2 sm:pt-5 sm:pb-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${column.sortable ? 'cursor-pointer hover:bg-gray-100 select-none' : ''}`}
                                            onClick={column.sortable ? () => handleSort(column.id) : undefined}
                                        >
                                            <div className="flex items-center space-x-1">
                                                <span>{column.label}</span>
                                                {column.sortable && getSortIcon(column.id)}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                        </table>
                    </div>
                </>
            )}
            <div>
                {/* Tabla de vehículos */}
                <div ref={scrollWrapperRef} className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead ref={theadRef} className="bg-gray-50 shadow-sm sticky top-0 z-10">
                            <tr>
                                {visibleColumns.map((column) => (
                                    <th
                                        key={column.id}
                                        className={`bg-gray-50 px-2 sm:px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${column.sortable ? 'cursor-pointer hover:bg-gray-200 select-none' : ''
                                            } ${isSalesDashboard ? 'pt-4 pb-2 sm:pt-5 sm:pb-3' : 'py-2 sm:py-3'}`}
                                        onClick={column.sortable ? () => handleSort(column.id) : undefined}
                                    >
                                        <div className="flex items-center space-x-1">
                                            <span>{column.label}</span>
                                            {column.sortable && getSortIcon(column.id)}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {paginatedVehiculos.map((vehicle, index) => {
                                let rowClass = `hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-25'}`;

                                if (isSalesDashboard) {
                                    const situacion = (vehicle.Situacion || vehicle.situacion || '').toLowerCase();
                                    if (situacion.includes('demo')) {
                                        rowClass = 'bg-yellow-100 hover:bg-yellow-200';
                                    }
                                } else {
                                    if (vehicle.estatus === ESTATUS.ENTREGADO_STANDBY) {
                                        rowClass = 'bg-red-100 hover:bg-red-200';
                                    } else if (vehicle.estatus === ESTATUS.FACTURADO_NO_ENTREGADO) {
                                        rowClass = 'bg-yellow-100 hover:bg-yellow-200';
                                    } else if (vehicle.NoMay) {
                                        rowClass = 'bg-blue-100 hover:bg-blue-200';
                                    }
                                }

                                return (
                                    <tr
                                        key={vehicle.VIN || index}
                                        className={`${rowClass} ${!disableRowClick ? 'cursor-pointer' : ''} transition-colors`}
                                        onClick={disableRowClick ? undefined : () => setSelectedVehicle(vehicle)}
                                    >
                                        {visibleColumns.map((column) => (
                                            <td
                                                key={`${vehicle.VIN}-${column.id}`}
                                                className="px-2 py-2 sm:px-4 sm:py-3 whitespace-nowrap text-sm text-gray-900"
                                            >
                                                {renderCell(vehicle, column.id)}
                                            </td>
                                        ))}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Controles de paginación */}
                {totalPages > 1 && (
                    <div className="mt-2 px-3 pb-3 sm:px-4 sm:pb-4 flex flex-col items-center gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <div className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
                            Mostrando {startIndex + 1}–{Math.min(endIndex, sortedVehiculos.length)} de {sortedVehiculos.length} vehículos
                        </div>

                        <div className="flex items-center gap-1">
                            <button
                                onClick={goToPrevPage}
                                disabled={currentPage === 1}
                                className={`px-2 py-1.5 sm:px-3 sm:py-2 rounded-lg border text-sm font-medium transition-colors ${currentPage === 1
                                    ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                    }`}
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </button>

                            {/* Mobile: fewer page numbers */}
                            <div className="flex items-center gap-1 sm:hidden">
                                {getPageNumbers(true).map((page, index) => (
                                    <React.Fragment key={index}>
                                        {page === '...' ? (
                                            <span className="px-1 py-1.5 text-gray-500 text-xs">…</span>
                                        ) : (
                                            <button
                                                onClick={() => goToPage(page)}
                                                className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${currentPage === page
                                                    ? 'bg-blue-600 text-white border border-blue-600'
                                                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                                    }`}
                                            >
                                                {page}
                                            </button>
                                        )}
                                    </React.Fragment>
                                ))}
                            </div>

                            {/* Desktop: more page numbers */}
                            <div className="hidden sm:flex items-center gap-1">
                                {getPageNumbers(false).map((page, index) => (
                                    <React.Fragment key={index}>
                                        {page === '...' ? (
                                            <span className="px-3 py-2 text-gray-500">...</span>
                                        ) : (
                                            <button
                                                onClick={() => goToPage(page)}
                                                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${currentPage === page
                                                    ? 'bg-blue-600 text-white border border-blue-600'
                                                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                                    }`}
                                            >
                                                {page}
                                            </button>
                                        )}
                                    </React.Fragment>
                                ))}
                            </div>

                            <button
                                onClick={goToNextPage}
                                disabled={currentPage === totalPages}
                                className={`px-2 py-1.5 sm:px-3 sm:py-2 rounded-lg border text-sm font-medium transition-colors ${currentPage === totalPages
                                    ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                    }`}
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                )}
                {/* Popup de Trazabilidad */}
                <CarTraceabilityPopUp
                    vehicle={selectedVehicle}
                    isOpen={!!selectedVehicle}
                    onClose={() => setSelectedVehicle(null)}
                />
            </div>
        </>
    );
};

export default VehiclesTable;